// src/pages/public/DataDeletion.js
// This page is REQUIRED by Facebook for apps using Facebook Login.
// Facebook will send users here when they want to delete their data.
// URL to enter in Facebook App Settings: https://www.aimbletech.com/data-deletion

import React, { useState } from "react";
import { motion } from "framer-motion";
import { addDoc, collection, serverTimestamp } from "firebase/firestore";
import { db } from "../../firebase";
import { COMPANY } from "../../utils/constants";

export default function DataDeletion() {
  const [form, setForm] = useState({ email: "", name: "", reason: "" });
  const [status, setStatus] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus("loading");
    try {
      await addDoc(collection(db, "data_deletion_requests"), {
        ...form,
        requestedAt: serverTimestamp(),
        status: "pending",
        source: "web_form",
      });
      setStatus("success");
    } catch (err) {
      console.error(err);
      setStatus("error");
    }
  };

  return (
    <div style={{ paddingTop: "var(--header-h)" }}>
      <section style={{ padding: "64px 0 96px" }}>
        <div className="container" style={{ maxWidth: 680 }}>
          <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }}>
            <div className="section-label">Your Data Rights</div>
            <h1 className="heading-1" style={{ marginBottom: 16 }}>
              Request Data Deletion
            </h1>
            <p style={{ color: "var(--text-muted)", lineHeight: 1.8, marginBottom: 40 }}>
              You have the right to request deletion of your personal data from Aimble I.T.
              Solutions at any time. This includes data collected when you registered using
              Facebook Login, Google Sign-In, or email.
            </p>

            {/* What we delete */}
            <div style={{
              background: "var(--surface)",
              border: "1px solid var(--border)",
              borderRadius: 16,
              padding: "24px 28px",
              marginBottom: 32,
            }}>
              <h2 style={{ fontSize: "1rem", fontWeight: 600, marginBottom: 16 }}>
                What gets deleted
              </h2>
              {[
                { icon: "👤", item: "Your account profile (name, email, profile photo)" },
                { icon: "📋", item: "Support tickets and messages you submitted" },
                { icon: "🔑", item: "Login credentials and social login connections" },
                { icon: "📦", item: "Order history linked to your account (anonymized, not erased — required for BIR compliance)" },
                { icon: "💳", item: "Payment references (anonymized after deletion — financial records required by Philippine law for 10 years)" },
              ].map(({ icon, item }) => (
                <div key={item} style={{ display: "flex", gap: 12, marginBottom: 12, fontSize: "0.875rem" }}>
                  <span style={{ flexShrink: 0 }}>{icon}</span>
                  <span style={{ color: "var(--text-muted)" }}>{item}</span>
                </div>
              ))}
            </div>

            {/* Timeline */}
            <div style={{
              background: "rgba(0,201,167,0.06)",
              border: "1px solid rgba(0,201,167,0.2)",
              borderRadius: 12,
              padding: "16px 20px",
              marginBottom: 40,
              display: "flex",
              gap: 12,
              alignItems: "flex-start",
            }}>
              <span style={{ fontSize: 20, flexShrink: 0 }}>⏱️</span>
              <div>
                <div style={{ fontWeight: 600, fontSize: "0.9375rem", marginBottom: 4 }}>
                  Processing time: up to 30 days
                </div>
                <p style={{ fontSize: "0.875rem", color: "var(--text-muted)", margin: 0 }}>
                  We will process your request within 30 days and send a confirmation to
                  your email once complete. If your account has active subscriptions or
                  pending payments, we will contact you first.
                </p>
              </div>
            </div>

            {/* Form */}
            {status === "success" ? (
              <div style={{
                background: "rgba(0,201,167,0.08)",
                border: "1px solid rgba(0,201,167,0.3)",
                borderRadius: 16,
                padding: "40px 32px",
                textAlign: "center",
              }}>
                <div style={{ fontSize: 40, marginBottom: 16 }}>✅</div>
                <h2 style={{ fontSize: "1.25rem", fontWeight: 700, marginBottom: 8 }}>
                  Request received
                </h2>
                <p style={{ color: "var(--text-muted)", marginBottom: 0 }}>
                  We've recorded your data deletion request. You'll receive a confirmation
                  at your email within 30 days. If you have questions, contact us at{" "}
                  <a href={`mailto:${COMPANY.email}`} style={{ color: "var(--accent)" }}>
                    {COMPANY.email}
                  </a>
                </p>
              </div>
            ) : (
              <div style={{
                background: "var(--surface)",
                border: "1px solid var(--border-med)",
                borderRadius: 16,
                padding: "32px 28px",
              }}>
                <h2 style={{ fontSize: "1rem", fontWeight: 600, marginBottom: 4 }}>
                  Submit a deletion request
                </h2>
                <p style={{ color: "var(--text-muted)", fontSize: "0.875rem", marginBottom: 24 }}>
                  Alternatively, you can email us directly at{" "}
                  <a href={`mailto:${COMPANY.email}`} style={{ color: "var(--accent)" }}>
                    {COMPANY.email}
                  </a>{" "}
                  with the subject line "Data Deletion Request".
                </p>

                {status === "error" && (
                  <div className="alert alert-error">
                    Something went wrong. Please email us at {COMPANY.email}
                  </div>
                )}

                <form onSubmit={handleSubmit}>
                  <div className="form-group">
                    <label className="form-label">Full Name *</label>
                    <input
                      className="form-input"
                      placeholder="As registered on your account"
                      value={form.name}
                      onChange={e => setForm(p => ({ ...p, name: e.target.value }))}
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Email Address *</label>
                    <input
                      type="email"
                      className="form-input"
                      placeholder="Email used for your account"
                      value={form.email}
                      onChange={e => setForm(p => ({ ...p, email: e.target.value }))}
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Reason (optional)</label>
                    <textarea
                      className="form-textarea"
                      placeholder="You don't have to provide a reason, but it helps us improve."
                      value={form.reason}
                      onChange={e => setForm(p => ({ ...p, reason: e.target.value }))}
                      style={{ minHeight: 90 }}
                    />
                  </div>
                  <button
                    type="submit"
                    className="btn btn-primary w-full"
                    style={{ justifyContent: "center" }}
                    disabled={status === "loading"}
                  >
                    {status === "loading" ? "Submitting..." : "Submit Deletion Request"}
                  </button>
                </form>
              </div>
            )}

            {/* Also disconnect from Facebook */}
            <div style={{
              marginTop: 32,
              padding: "20px 24px",
              background: "var(--surface)",
              border: "1px solid var(--border)",
              borderRadius: 12,
            }}>
              <h3 style={{ fontSize: "0.9375rem", fontWeight: 600, marginBottom: 8 }}>
                Also disconnect from Facebook
              </h3>
              <p style={{ fontSize: "0.875rem", color: "var(--text-muted)", marginBottom: 0 }}>
                To revoke AimbleTech's access to your Facebook account, go to:{" "}
                <strong style={{ color: "var(--text)" }}>
                  Facebook → Settings → Security and Login → Apps and Websites → Remove AimbleTech
                </strong>
              </p>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
